import { safeHref } from '@/features/storefront/sections/primitives';

/**
 * What a social links section knows about social networks.
 *
 * Three callers need the same answers and would each get them slightly wrong
 * on their own: the inspector, which has to tell a merchant their link is not
 * a link *while they are still typing it*; the renderer, which has to decide
 * whether an icon is shown at all; and the schema, which has to agree with the
 * server about what it will accept. Mirrors `social_link_href` and the
 * `SocialLinkBlock` rules in `app/schemas/store.py` - the server is what
 * validates a save, so a disagreement here shows up as a section the
 * customiser accepted and the API refused.
 *
 * No JSX lives here. The glyphs are in `SocialIcon.tsx`, because this module is
 * imported by `schema.ts`, which the storefront parses documents with long
 * before anything is drawn.
 */

/**
 * The networks a shop can link to.
 *
 * A closed set, for the same reason section types are closed: each one has an
 * official mark, and an open list of names would mean drawing an icon for a
 * string a merchant typed - which is either a wrong logo or no logo at all.
 * `custom` is the escape hatch and is explicitly *not* a brand: it is a link
 * with a neutral glyph and a name the merchant writes, which is the honest
 * answer for the network we do not ship a mark for.
 */
export const SOCIAL_PLATFORMS = [
  'youtube',
  'facebook',
  'linkedin',
  'x',
  'instagram',
  'pinterest',
  'whatsapp',
  'custom',
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/**
 * The rows a new section arrives with, in the order it offers them.
 *
 * Every one of them empty. A merchant adding this section is not asked to go
 * and find the platform in a menu first - the seven networks a shop actually
 * has are already listed, each with its mark, waiting for a URL. That is the
 * whole of what makes this section quick to fill in, and it costs nothing on
 * the shopper's page: a row with no link is not rendered.
 *
 * `custom` is not among them. It is a row a merchant adds when they need it,
 * and one that arrived by default would be an unnamed link with nothing in it.
 */
export const DEFAULT_SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  'youtube',
  'facebook',
  'linkedin',
  'x',
  'instagram',
  'pinterest',
  'whatsapp',
];

/**
 * What each network is called, in the words a merchant would use.
 *
 * X keeps Twitter in its label on purpose. The company renamed itself; the
 * merchants looking for it in a dropdown mostly have not, and a list where
 * "Twitter" matches nothing reads as a list that is missing it.
 */
export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  youtube: 'YouTube',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X (Twitter)',
  instagram: 'Instagram',
  pinterest: 'Pinterest',
  whatsapp: 'WhatsApp',
  custom: 'Other',
};

/**
 * Each network's own colour, for the sections drawn in brand colours.
 *
 * Instagram is a gradient rather than a colour, and this is the flat value its
 * own brand guidelines give for the case where a gradient cannot be used - the
 * renderer paints the gradient where it can and falls back to this. `custom`
 * has no brand, so it takes the theme's own accent and is the one platform
 * that looks the same either way.
 */
export const SOCIAL_PLATFORM_COLORS: Record<SocialPlatform, string> = {
  youtube: '#FF0000',
  facebook: '#0866FF',
  linkedin: '#0A66C2',
  x: '#000000',
  instagram: '#E4405F',
  pinterest: '#BD081C',
  whatsapp: '#25D366',
  custom: 'var(--storefront-primary)',
};

/**
 * What a merchant sees in the empty URL field.
 *
 * A real example of the address they are being asked for, rather than the word
 * "URL": the merchant who does not know where to find their page ID learns
 * more from the shape of the answer than from the name of the question.
 */
export const SOCIAL_PLATFORM_PLACEHOLDERS: Record<SocialPlatform, string> = {
  youtube: 'https://www.youtube.com/@yourshop',
  facebook: 'https://www.facebook.com/yourshop',
  linkedin: 'https://www.linkedin.com/company/yourshop',
  x: 'https://x.com/yourshop',
  instagram: 'https://www.instagram.com/yourshop',
  pinterest: 'https://www.pinterest.com/yourshop',
  whatsapp: 'https://wa.me/919876543210',
  custom: 'https://example.com/yourshop',
};

/**
 * A phone number a merchant typed instead of a wa.me link.
 *
 * WhatsApp is the one network on this list with no profile page to copy an
 * address from. What a shop has is its number, and the link is that number
 * with `https://wa.me/` in front of it - a fact the merchant has no reason to
 * know, and being made to look it up is the difference between this row being
 * filled in and being left empty.
 *
 * Digits, with the separators a number is written with. Six to fifteen of them
 * because E.164 tops out at fifteen and a shorter run is a typo rather than a
 * number - so `+91 98765 43210` is understood and `12` is not.
 */
const PHONE_NUMBER = /^\+?[\d\s().-]{6,24}$/;

/**
 * Anything already carrying a scheme, or rooted at the store.
 *
 * Such a value is judged by `safeHref` instead, which is what tells `tel:` and
 * `javascript:` apart rather than mistaking either for a phone number. Kept
 * identical to `_HAS_SCHEME` on the server.
 */
const HAS_SCHEME = /^(?:[a-z][a-z0-9+.-]*:|\/)/i;

function whatsappHref(trimmed: string): string {
  if (!PHONE_NUMBER.test(trimmed)) return '';
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 6 && digits.length <= 15 ? `https://wa.me/${digits}` : '';
}

/**
 * Where one social link actually points, or `''` when it points nowhere.
 *
 * The single answer to "is this link configured": the renderer draws an icon
 * when this returns something and skips the row when it does not, so an empty
 * or half-typed field is a missing icon rather than a dead one. The
 * merchant-authored href rule is the same one every other link in a theme
 * document obeys - see `safeHref` - which is what keeps a `javascript:` URL
 * from reaching an `<a>` by way of a route nobody was watching.
 *
 * The stored value is what the merchant typed, never what this returns. A
 * merchant who pasted an address should find that address waiting when they
 * come back to edit it, the same way a video section keeps the YouTube link
 * rather than the id it embeds.
 */
export function socialLinkHref(
  platform: SocialPlatform,
  url: string | null | undefined,
): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return '';
  if (platform === 'whatsapp' && !HAS_SCHEME.test(trimmed)) return whatsappHref(trimmed);
  return safeHref(trimmed);
}

/**
 * Whether a merchant's half-typed link should be complained about yet.
 *
 * Empty is not an error - a section arrives with seven rows nobody has filled
 * in, and seven fields turning red on sight is a panel that shouts at a
 * merchant for having just added it. Anything else either resolves to an
 * address or does not.
 */
export function isSocialUrlValid(
  platform: SocialPlatform,
  url: string | null | undefined,
): boolean {
  const trimmed = (url ?? '').trim();
  return trimmed === '' || socialLinkHref(platform, trimmed) !== '';
}

/**
 * What one link is called, to a merchant reading the list and to a screen
 * reader reading the page.
 *
 * The platform's name unless the merchant wrote their own, which is what makes
 * `custom` usable at all: a row labelled "Other" three times over is a list
 * nobody can navigate. Every icon on the storefront is announced with this,
 * because an anchor whose only content is an SVG is otherwise announced as
 * "link" and nothing else.
 */
export function socialLinkLabel(link: { platform: SocialPlatform; label: string }): string {
  const own = link.label.trim();
  if (own) return own;
  return SOCIAL_PLATFORM_LABELS[link.platform];
}

/**
 * How the icons are drawn, and how big.
 *
 * Named rather than measured for the same reason padding is: the renderer owns
 * the pixels, so a merchant cannot leave a row of 4px icons or a strip that
 * pushes the rest of the page off the screen.
 *
 * `plain` is the mark on its own, `outlined` sets it in a ring, and `filled`
 * knocks it out of a solid disc. Three rather than one because the same row of
 * icons has to sit under a heading on a pale page and inside a dark footer
 * band, and a mark that reads on one disappears on the other.
 */
export const SOCIAL_ICON_STYLES = ['plain', 'outlined', 'filled'] as const;
export const SOCIAL_ICON_SIZES = ['sm', 'md', 'lg'] as const;
export const SOCIAL_ALIGNMENTS = ['left', 'center', 'right'] as const;

export type SocialIconStyle = (typeof SOCIAL_ICON_STYLES)[number];
export type SocialIconSize = (typeof SOCIAL_ICON_SIZES)[number];
export type SocialAlignment = (typeof SOCIAL_ALIGNMENTS)[number];

export const SOCIAL_ICON_STYLE_LABELS: Record<SocialIconStyle, string> = {
  plain: 'Just the icon',
  outlined: 'Icon in a ring',
  filled: 'Icon on a solid circle',
};

export const SOCIAL_ICON_SIZE_LABELS: Record<SocialIconSize, string> = {
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
};

export const SOCIAL_ALIGNMENT_LABELS: Record<SocialAlignment, string> = {
  left: 'Left',
  center: 'Centred',
  right: 'Right',
};

/**
 * The pixels behind each size, as the glyph and as the shape around it.
 *
 * Two numbers rather than one because `plain` has no shape: the glyph is the
 * whole of what is drawn, so it stands at the larger measure and the ring or
 * disc is what grows around it in the other two styles. The tap target is the
 * `box` value, which is why the smallest of them is still 36px - below that a
 * row of icons is a row of things a thumb misses.
 */
export const SOCIAL_ICON_PIXELS: Record<SocialIconSize, { glyph: number; box: number }> = {
  sm: { glyph: 18, box: 36 },
  md: { glyph: 22, box: 44 },
  lg: { glyph: 28, box: 54 },
};
